/**
 * registry.ts —— Host Registry（宿主能力注册表）
 *
 * 这是整个项目**最关键的一个文件**，因为架构原则 2 说：
 *
 *   Host Registry 既是执行树，也是 describeForPrompt() 的唯一数据源。
 *   新增一个 API，AI 立刻就会用 —— 人不需要同步维护任何文档。
 *
 * 怎么做到「一份数据、两个用途」：
 *   每个 method 注册时同时声明三样东西 ——
 *     · run(...)        真正要做什么（执行树用）
 *     · params + desc   参数是什么、干什么用的（提示词用）
 *     · successText     成功后怎么说（信任原则用）
 *   三者写在**同一个对象字面量里**，物理上无法分家。
 *
 * 关于 successText 为什么要和 run 写在一起（设计文档 2.1.1）：
 *   如果把成功文案集中写在别处，时间一长就会与真实行为脱节 ——
 *   代码改了但文案忘了改，于是 AI 说「已切换」而实际没切。
 *   写在一起时，改动 run 的人必然看到旁边那行 successText，
 *   想不改都难。这是用**代码结构**强制信任原则，而不是靠自觉。
 *
 * 「无 mock 原则」(D5) 的落点也在这里：
 *   注册进来的每一条都必须是真实现。做不到的能力**不注册** ——
 *   AI 不知道它存在，用户提到时它会诚实回 explain('做不到')。
 */

import type { ParamSpec, EvalError } from '@/types/il';

/** 一次方法调用需要的上下文 */
export interface CallContext {
  /**
   * 读取当前分支已有的值。
   * 链式调用里，前一个方法的返回值会成为后一个方法的 this 值 ——
   * 这是 IL 支持 `file.pick().summarize()` 这类链的机制。
   */
  readonly value: unknown;
  /** 已注册的方法集合，供需要动态分发的地方使用 */
  readonly registry: Registry;
}

/** 一次方法调用的结果 */
export interface CallOutcome {
  /** 返回值。为 undefined 表示无返回值（链到此为止） */
  value?: unknown;
  /** 成功文案。省略则用 method 的 successText */
  text?: string;
}

/** 一个方法的完整定义 */
export interface MethodDef {
  /** 方法名，如 setTheme */
  name: string;
  /**
   * 方法类型，决定 AI 该怎么用它 —— **会进入提示词**。
   *
   * · 'action'（默认）：改变状态的操作，如 setTheme('dark')。
   *   用户说「调成黑夜模式」→ 输出这一条就够了，成功文案由程序生成。
   *
   * · 'query'：只读取数据、不改状态，返回值是对用户提问的回答，
   *   如 contact.list()。用户问「有哪些联系人」时**不能只输出这一条** ——
   *   那样用户只会看到「已读取联系人列表」，而不是真正的名单。
   *   query 必须与 explain 配对使用：先拿到数据，再用 explain 念给用户。
   *
   * 之所以要在注册表里显式区分，而不是靠方法名猜（list/get/find）：
   *   命名约定是隐式的、会漂移的；而 kind 是数据本身，
   *   和 desc / successText 一样写在同一个对象字面量里，不会两头脱节。
   */
  kind?: 'action' | 'query';
  /** 一句话说明它做什么。**会进入提示词**，所以要写给模型看 */
  desc: string;
  /** 参数定义。**会进入提示词** */
  params?: ParamSpec[];
  /**
   * 成功后的中文描述（信任原则）。
   * 可以是字符串，也可以是函数 —— 需要引用实参时用函数形式。
   */
  successText: string | ((args: unknown[]) => string);
  /** 真正的执行体 */
  run: (args: unknown[], ctx: CallContext) => CallOutcome | void | Promise<CallOutcome | void>;
  /**
   * 需要用户二次确认时返回确认文案（返回 undefined 表示不需要）。
   * 由求值器负责弹窗，确认后才执行 run —— 见 design.md 的 ConfirmRequired。
   */
  requireConfirm?: (args: unknown[]) => string | undefined;
}

/** 一个根对象的定义，如 setting / nav */
export interface ObjectDef {
  /** 根对象名，如 setting */
  name: string;
  /** 说明它管什么。**会进入提示词**，写给模型看 */
  desc: string;
  /**
   * 给**用户**看的一句话（用于 app.help 这类面向人的输出）。
   * 不填则退回 desc。
   *
   * 为什么要分两份描述：
   *   desc 是给模型看的，用第三人称、可以提「用户」「返回值」这类词；
   *   用户看 help 时不该读到「向用户说一句话」这种开发者腔。
   *   两份描述都写在同一个对象里，不会两头脱节。
   */
  userDesc?: string;
  /**
   * 是否在面向用户的 help 里隐藏。
   * 用于 explain 这类**内部机制**：它是 IL 的语法糖出口，
   * 对用户而言不是「一项能力」，列出来只会让人困惑。
   */
  hidden?: boolean;
  methods: MethodDef[];
}

/**
 * 执行中抛出的业务错误。
 * 与 LexError / ParseError 的区别：这是「语法没问题但做不了」。
 */
export class RuntimeError extends Error {
  constructor(
    message: string,
    public readonly kind: 'NameError' | 'TypeError' | 'RuntimeError' = 'RuntimeError',
  ) {
    super(message);
    this.name = 'RuntimeError';
  }

  toEvalError(): EvalError {
    return { kind: this.kind, message: this.message };
  }
}

export class Registry {
  private readonly objects = new Map<string, ObjectDef>();
  private readonly methodIndex = new Map<string, MethodDef>(); // key: `root.method`
  /** 语法糖支持：根对象名 → 省略方法名时使用的默认方法 */
  private readonly defaultMethods = new Map<string, string>();

  /** 注册一个根对象及其全部方法 */
  registerObject(def: ObjectDef): this {
    this.objects.set(def.name, def);
    for (const m of def.methods) {
      this.methodIndex.set(`${def.name}.${m.name}`, m);
    }
    return this;
  }

  /**
   * 声明某根对象的默认方法。
   *
   * 用途：让 `explain('文字')` 这种省略方法名的写法可用
   * （解析器产出空方法名的 call，求值器来这里查该用什么）。
   *
   * 只对**高频且易于写错**的对象声明 —— 目前只有 explain。
   * 滥用会让 IL 出现两种等价写法，反倒增加模型的心智负担。
   */
  setDefaultMethod(root: string, method: string): this {
    this.defaultMethods.set(root, method);
    return this;
  }

  /** 查某根对象的默认方法名，没有则返回空串 */
  getDefaultMethod(root: string): string {
    return this.defaultMethods.get(root) ?? '';
  }

  /** 查一个方法定义 */
  lookup(root: string, method: string): MethodDef | undefined {
    return this.methodIndex.get(`${root}.${method}`);
  }

  /** 某个根对象下的全部方法（用于 NameError 时列出可用项） */
  lookupAll(root: string): MethodDef[] {
    return this.objects.get(root)?.methods ?? [];
  }

  /** 根对象是否存在 */
  hasObject(root: string): boolean {
    return this.objects.has(root);
  }

  /** 列出全部根对象名（按注册顺序） */
  listObjects(): ObjectDef[] {
    return [...this.objects.values()];
  }

  /** 已注册的方法总数，用于自检 */
  countMethods(): number {
    return this.methodIndex.size;
  }

  /**
   * 生成提示词里的能力清单。
   *
   * ⚠️ 这是「单一真相源」的出口 —— 提示词里的能力描述**只能**来自这里。
   * 谁要是在别处手写一份能力文档，就是给这个项目埋了一颗雷：
   * 两边迟早对不上，然后 AI 开始用不存在的方法。
   *
   * 输出格式刻意做得紧凑（每行一条），因为提示词长度直接等于 token 成本。
   */
  describeForPrompt(): string {
    const blocks: string[] = [];

    for (const obj of this.objects.values()) {
      // hidden 的对象不进提示词也不太对 —— explain 是模型必须知道的出口。
      // 所以 hidden 只影响「面向用户的 help」，不影响提示词。这里照写。
      blocks.push(`${obj.name} —— ${obj.desc}`);
      for (const m of obj.methods) {
        // query 打上 [查] 标记，提示模型：这一条只拿数据，还得走回答轮说出来
        const tag = m.kind === 'query' ? '[查] ' : '';
        blocks.push(`  ${tag}${obj.name}.${m.name}(${formatParams(m.params)})`);
        blocks.push(`    ${m.desc}`);
      }
      blocks.push('');
    }

    return blocks.join('\n').trimEnd();
  }

  /**
   * 生成**面向用户**的能力说明（app.help 用）。
   *
   * 与 describeForPrompt 的区别：
   *   · 跳过 hidden 的对象（如 explain —— 那是内部机制，不是"一项能力"）
   *   · 用 userDesc 而非 desc，读起来不像开发者文档
   *
   * 数据仍然全部来自 registry，所以加 API 时这里不用改。
   */
  describeForUser(): string[] {
    return [...this.objects.values()]
      .filter((o) => !o.hidden)
      .map((o) => o.userDesc ?? o.desc);
  }
}

/**
 * 把参数规格渲染成签名形式。
 * 例：`(mode: 'light' | 'dark' | 'auto')`、`(name: string)`
 */
function formatParams(params?: ParamSpec[]): string {
  if (!params || params.length === 0) return '';

  return params
    .map((p) => {
      let typeText: string;
      if (p.type === 'enum' && p.values?.length) {
        typeText = p.values.map((v) => `'${v}'`).join(' | ');
      } else if (p.type === 'handle') {
        typeText = p.handleKind ? `${p.handleKind}Handle` : 'handle';
      } else {
        typeText = p.type;
      }

      const optional = p.required === false ? '?' : '';
      const range =
        p.type === 'number' && (p.min !== undefined || p.max !== undefined)
          ? `  ${p.min ?? ''}~${p.max ?? ''}`
          : '';
      const note = p.desc ? `  ${p.desc}` : '';

      return `${p.name}${optional}: ${typeText}${range}${note}`;
    })
    .join(', ');
}

/** 全局唯一的注册表实例 */
export const registry = new Registry();
